import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import { useAuthStore } from './authStore'

const SELECTED_COMPANY_KEY = 'chemtrace_selected_company_id'

export interface Company {
  id: string
  name: string
  role: string
}

export const useCompanyStore = defineStore('company', () => {
  const authStore = useAuthStore()

  // State
  const selectedCompanyId = ref<string | null>(null)

  // Getters
  const userCompanies = computed<Company[]>(() => {
    return authStore.user?.companies || []
  })

  const selectedCompany = computed<Company | null>(() => {
    if (!selectedCompanyId.value) return null
    return userCompanies.value.find(c => c.id === selectedCompanyId.value) || null
  })

  const hasMultipleCompanies = computed(() => userCompanies.value.length > 1)

  const hasCompanies = computed(() => userCompanies.value.length > 0)

  // Actions
  function selectCompany(id: string | null) {
    selectedCompanyId.value = id
    if (id) {
      localStorage.setItem(SELECTED_COMPANY_KEY, id)
    } else {
      localStorage.removeItem(SELECTED_COMPANY_KEY)
    }
  }

  function initFromStorage() {
    // If user is not loaded yet, wait
    if (!authStore.user) return

    const companies = userCompanies.value
    if (companies.length === 0) {
      selectedCompanyId.value = null
      return
    }

    // Try to restore from localStorage
    const stored = localStorage.getItem(SELECTED_COMPANY_KEY)
    if (stored && companies.some(c => c.id === stored)) {
      selectedCompanyId.value = stored
    } else {
      // Default to primary company (first in list)
      selectedCompanyId.value = companies[0].id
      localStorage.setItem(SELECTED_COMPANY_KEY, companies[0].id)
    }
  }

  function clearSelection() {
    selectedCompanyId.value = null
    localStorage.removeItem(SELECTED_COMPANY_KEY)
  }

  // Watch for auth changes to re-init
  watch(() => authStore.user, (user) => {
    if (user) {
      initFromStorage()
    } else {
      clearSelection()
    }
  }, { immediate: true })

  return {
    // State
    selectedCompanyId,
    // Getters
    userCompanies,
    selectedCompany,
    hasMultipleCompanies,
    hasCompanies,
    // Actions
    selectCompany,
    initFromStorage,
    clearSelection,
  }
})
